import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Search, ShieldCheck, Activity } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { LogPulseLogo } from './LogPulseLogo';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

interface Exchange {
  q: string;
  a: string;
  statusKey: string;
  icon: LucideIcon;
}

/**
 * The hero demo for a non-technical audience: a plain-language question typed
 * into LogPulse and a plain-language answer coming back. No query syntax, no
 * charts; the product promise ("ask your logs anything") acted out. Cycles
 * through three exchanges; respects prefers-reduced-motion by showing the
 * first exchange statically.
 */
const EXCHANGES: Omit<Exchange, 'q' | 'a'>[] = [
  { statusKey: 'status1', icon: Search },
  { statusKey: 'status2', icon: ShieldCheck },
  { statusKey: 'status3', icon: Activity },
];

type Phase = 'typing' | 'thinking' | 'answered';

export function HeroAskDemo() {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('typing');
  const [reduced, setReduced] = useState(false);
  const timers = useRef<number[]>([]);

  const exchanges: Exchange[] = EXCHANGES.map((e, i) => ({
    ...e,
    q: t(`heroDemo.q${i + 1}`),
    a: t(`heroDemo.a${i + 1}`),
  }));
  const current = exchanges[index];

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      setTyped(exchanges[0].q);
      setPhase('answered');
      setIndex(0);
      return;
    }
    const schedule = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };
    const question = current.q;
    setTyped('');
    setPhase('typing');
    for (let i = 1; i <= question.length; i++) {
      schedule(() => setTyped(question.slice(0, i)), 350 + i * 32);
    }
    const doneTyping = 350 + question.length * 32;
    schedule(() => setPhase('thinking'), doneTyping + 250);
    schedule(() => setPhase('answered'), doneTyping + 1500);
    schedule(() => setIndex((index + 1) % exchanges.length), doneTyping + 7500);
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, reduced]);

  return (
    <div className="relative rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl shadow-gray-950/5 dark:shadow-none overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
        <LogPulseLogo className="h-5 w-5" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white">{t('heroDemo.header')}</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
          {t('heroDemo.live')}
        </span>
      </div>

      {/*
        Every exchange is laid out invisibly in the same grid cell as the live
        one, so the card is always as tall as the longest question + answer.
        Without that, the card grows while typing and shrinks between exchanges,
        which on mobile (demo stacked above the rest of the page) pushes the
        whole page up and down.
      */}
      <div className="grid p-5 sm:p-6">
        {exchanges.map((exchange, i) => (
          <div key={i} className="col-start-1 row-start-1 invisible" aria-hidden="true">
            <ExchangeBody exchange={exchange} question={exchange.q} t={t} />
          </div>
        ))}

        <div className="col-start-1 row-start-1">
          <ExchangeBody
            exchange={current}
            question={typed}
            answered={phase === 'answered'}
            cursor={phase === 'typing' && !reduced}
            thinking={phase === 'thinking'}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}

interface ExchangeBodyProps {
  exchange: Exchange;
  /** Question text to render; the live copy shows it typed out character by character. */
  question: string;
  answered?: boolean;
  cursor?: boolean;
  thinking?: boolean;
  t: (key: string) => string;
}

/**
 * One question + answer pair. Rendered once per exchange as an invisible height
 * placeholder (defaults: answer shown, no cursor or thinking dots) and once for
 * the exchange currently playing.
 */
function ExchangeBody({ exchange, question, answered = true, cursor = false, thinking = false, t }: ExchangeBodyProps) {
  const Icon = exchange.icon;

  return (
    <>
      {/* Question being typed */}
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-semibold text-gray-500 dark:text-gray-400" aria-hidden="true">
          {t('heroDemo.youInitial')}
        </div>
        <p className="pt-1.5 text-[15px] font-medium text-gray-900 dark:text-white">
          {question}
          {cursor && (
            <span className="inline-block w-[2px] h-[1.1em] align-text-bottom bg-primary-500 ml-0.5 animate-pulse" />
          )}
        </p>
      </div>

      <div className="relative mt-4 ml-11">
        {/* Thinking indicator, overlaid on the space the answer already reserves */}
        {thinking && (
          <div className="absolute inset-x-0 top-0 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 animate-fade-in">
            <span className="flex gap-1" aria-hidden="true">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-bounce" />
              <span className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '120ms' }} />
              <span className="h-1.5 w-1.5 rounded-full bg-primary-400 animate-bounce" style={{ animationDelay: '240ms' }} />
            </span>
            {t('heroDemo.checking')}
          </div>
        )}

        {/* Answer */}
        <div
          className={cn(
            'transition-all duration-500',
            answered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
          )}
          aria-hidden={!answered}
        >
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                {t(`heroDemo.${exchange.statusKey}`)}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{exchange.a}</p>
            <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              <CheckCircle2 className="h-3.5 w-3.5 text-success-500" aria-hidden="true" />
              {t('heroDemo.evidence')}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
